import { Box, Grid, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useSegments } from "api/segments";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ReactComponent as Copy02Icon } from "components/Icons/copy-02.svg";
import { ReactComponent as FilePlus03Icon } from "components/Icons/file-plus-03.svg";
import { ReactComponent as Pencil02Icon } from "components/Icons/pencil-02.svg";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as Trash02Icon } from "components/Icons/trash-02.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import {
  Parts,
  SegmentFECondition,
  SegmentFEFields,
  SegmentFEParts,
  SegmentType,
  SegmentTypes,
} from "types/segments";
import { Age, initialValuesAge } from "./SegmentConditions/Age";
import {
  AverageTransactionAmount,
  initialValuesAverageTransactionAmount,
} from "./SegmentConditions/AverageTransactionAmount";
import {
  BoughtInSpecificChannel,
  initialValuesBoughtInSpecificChannel,
} from "./SegmentConditions/BoughtInSpecificChannel";
import {
  BoughtProductsWithLabels,
  initialValuesBoughtProductsWithLabels,
} from "./SegmentConditions/BoughtProductsWithLabels";
import {
  BoughtSpecificBrands,
  initialValuesBoughtSpecificBrands,
} from "./SegmentConditions/BoughtSpecificBrands";
import {
  BoughtSpecificSKU,
  initialValuesBoughtSpecificSKU,
} from "./SegmentConditions/BoughtSpecificSKU";
import {
  CampaignCompletion,
  initialValuesCampaignCompletion,
} from "./SegmentConditions/CampaignCompletion";
import { City, initialValuesCity } from "./SegmentConditions/City";
import {
  CountryList,
  initialValuesCountry,
} from "./SegmentConditions/CountryList";
import {
  CustomMemberList,
  initialValuesCustomMemberList,
} from "./SegmentConditions/CustomMemberList";
import {
  DailyGrossValueInGivenPeriod,
  initialValuesDailyGrossValueInGivenPeriod,
} from "./SegmentConditions/DailyGrossValueInGivenPeriod";
import {
  DailyGrossValueInLastNDays,
  initialValuesDailyGrossValueInLastNDays,
} from "./SegmentConditions/DailyGrossValueInLastNDays";
import { Gender, initialValuesGender } from "./SegmentConditions/Gender";
import {
  LastPurchaseWasNDaysAgo,
  initialValuesLastPurchaseWasNDaysAgo,
} from "./SegmentConditions/LastPurchaseWasNDaysAgo";
import {
  MemberRegistrationDate,
  initialValuesMemberRegistrationDate,
} from "./SegmentConditions/MemberRegistrationDate";
import {
  MembersHaveSuchLabelsValues,
  initialValuesMembersHaveSuchLabelsValues,
} from "./SegmentConditions/MembersHaveSuchLabelsValues";
import {
  MembersWhoHaveSuchLabels,
  initialValuesMembersWhoHaveSuchLabels,
} from "./SegmentConditions/MembersWhoHaveSuchLabels";
import {
  PostalCode,
  initialValuesPostalCode,
} from "./SegmentConditions/PostalCode";
import { Province, initialValuesProvince } from "./SegmentConditions/Province";
import {
  PurchasePeriod,
  initialValuesPurchasePeriod,
} from "./SegmentConditions/PurchasePeriod";
import {
  RegistrationAnniversary,
  initialValuesRegistrationAnniversary,
} from "./SegmentConditions/RegistrationAnniversary";
import {
  TotalGrossValueInGivenPeriod,
  initialValuesTotalGrossValueInGivenPeriod,
} from "./SegmentConditions/TotalGrossValueInGivenPeriod";
import {
  TotalGrossValueInLastNDays,
  initialValuesTotalGrossValueInLastNDays,
} from "./SegmentConditions/TotalGrossValueInLastNDays";
import {
  TransactionCount,
  initialValuesTransactionCount,
} from "./SegmentConditions/TransactionCount";
import {
  TransactionPercentInChannel,
  initialValuesTransactionPercentInChannel,
} from "./SegmentConditions/TransactionPercentInChannel";
import {
  TransactionValue,
  initialValuesTransactionValue,
} from "./SegmentConditions/TransactionValue";

import { ExternalLink } from "ExternalLink";
import { EmptyState } from "components/EmptyState/EmptyState";
import { FormButtons } from "components/FormControls/FormButtons";
import { ItemTypeCheckbox } from "components/FormControls/ItemTypeCheckbox";
import { CommandBar } from "components/Tooltips/CommandBar/CommandBar";
import { surveyQuery } from "constants/general";
import { FieldArray, Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import _, { omit } from "lodash";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { alphabetizeByProperty } from "utilities/arrays";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import {
  AchievementCompletion,
  initialValuesAchievementCompletion,
} from "./SegmentConditions/AchievementCompletion";
import {
  AchievementProgression,
  initialValuesAchievementProgression,
} from "./SegmentConditions/AchievementProgression";
import {
  BirthdayAnniversary,
  initialValuesBirthdayAnniversary,
} from "./SegmentConditions/BirthdayAnniversary";
import {
  LastCustomEventWasNDaysAgo,
  initialValuesLastCustomEventWasNDaysAgo,
} from "./SegmentConditions/LastCustomEventWasNDaysAgo";
import {
  LastCustomEventWithin,
  initialValuesLastCustomEventWithin,
} from "./SegmentConditions/LastCustomEventWithin";
import {
  LastPurchaseWithin,
  initialValuesLastPurchaseWithin,
} from "./SegmentConditions/LastPurchaseWithin";
import {
  MemberTiers,
  initialValuesMemberTiers,
} from "./SegmentConditions/MemberTiers";
import {
  NumberOfTransactionsInGivenPeriod,
  initialValuesNumberOfTransactionsInGivenPeriod,
} from "./SegmentConditions/NumberOfTransactionsInGivenPeriod";
import { useStyles } from "./SegmentForm.styles";

export const segmentConditions: SegmentFECondition[] = [
  // member details
  {
    component: AchievementProgression,
    initialValues: initialValuesAchievementProgression,
    label: "achievementProgression",
    category: "memberDetails",
  },
  {
    component: AchievementCompletion,
    initialValues: initialValuesAchievementCompletion,
    label: "achievementCompletion",
    category: "memberDetails",
  },
  {
    component: Age,
    initialValues: initialValuesAge,
    label: "age",
    category: "memberDetails",
  },
  {
    component: BirthdayAnniversary,
    initialValues: initialValuesBirthdayAnniversary,
    label: "birthdayInXNumberOfDays",
    category: "memberDetails",
  },
  {
    component: CampaignCompletion,
    initialValues: initialValuesCampaignCompletion,
    label: "campaignCompletion",
    category: "memberDetails",
  },
  {
    component: City,
    initialValues: initialValuesCity,
    label: "city",
    category: "memberDetails",
  },
  {
    component: CountryList,
    initialValues: initialValuesCountry,
    label: "country",
    category: "memberDetails",
  },
  {
    component: CustomMemberList,
    initialValues: initialValuesCustomMemberList,
    label: "customMembersList",
    category: "memberDetails",
  },
  {
    component: Gender,
    initialValues: initialValuesGender,
    label: "gender",
    category: "memberDetails",
  },
  {
    component: MemberTiers,
    initialValues: initialValuesMemberTiers,
    label: "memberTiers",
    category: "memberDetails",
  },
  {
    component: MembersHaveSuchLabelsValues,
    initialValues: initialValuesMembersHaveSuchLabelsValues,
    label: "membersWithCustomAttributeValue",
    category: "memberDetails",
  },
  {
    component: MembersWhoHaveSuchLabels,
    initialValues: initialValuesMembersWhoHaveSuchLabels,
    label: "membersWithCustomAttribute",
    category: "memberDetails",
  },
  {
    component: RegistrationAnniversary,
    initialValues: initialValuesRegistrationAnniversary,
    category: "memberDetails",
    label: "registrationDateInXNumberOfDays",
  },
  {
    component: MemberRegistrationDate,
    initialValues: initialValuesMemberRegistrationDate,
    category: "memberDetails",
    label: "memberRegistrationDate",
  },
  {
    component: PostalCode,
    initialValues: initialValuesPostalCode,
    category: "memberDetails",
    label: "postalCode",
  },
  {
    component: Province,
    initialValues: initialValuesProvince,
    category: "memberDetails",
    label: "province",
  },
  // transaction
  {
    component: TransactionPercentInChannel,
    initialValues: initialValuesTransactionPercentInChannel,
    label: "percentOfPurchaseTransactionsInChannel",
    category: "purchaseTransaction",
  },
  {
    component: AverageTransactionAmount,
    initialValues: initialValuesAverageTransactionAmount,
    label: "averagePurchaseTransactionValueRange",
    category: "purchaseTransaction",
  },
  {
    component: BoughtInSpecificChannel,
    initialValues: initialValuesBoughtInSpecificChannel,
    label: "boughtInSpecificChannel",
    category: "purchaseTransaction",
  },
  {
    component: BoughtProductsWithLabels,
    initialValues: initialValuesBoughtProductsWithLabels,
    label: "boughtProductsWithCustomAttributes",
    category: "purchaseTransaction",
  },
  {
    component: BoughtSpecificBrands,
    initialValues: initialValuesBoughtSpecificBrands,
    label: "boughtSpecificBrands",
    category: "purchaseTransaction",
  },
  {
    component: BoughtSpecificSKU,
    initialValues: initialValuesBoughtSpecificSKU,
    label: "boughtSpecificSKU",
    category: "purchaseTransaction",
  },
  {
    component: DailyGrossValueInGivenPeriod,
    initialValues: initialValuesDailyGrossValueInGivenPeriod,
    label: "dailyGrossValueInGivenPeriod",
    category: "purchaseTransaction",
  },
  {
    component: DailyGrossValueInLastNDays,
    initialValues: initialValuesDailyGrossValueInLastNDays,
    label: "dailyGrossValueInLastXDays",
    category: "purchaseTransaction",
  },
  {
    component: LastPurchaseWithin,
    initialValues: initialValuesLastPurchaseWithin,
    label: "lastPurchaseTransactionWithinTheXToYNumberOfDays",
    category: "purchaseTransaction",
  },
  {
    component: TotalGrossValueInGivenPeriod,
    initialValues: initialValuesTotalGrossValueInGivenPeriod,
    label: "totalSpendingInGivenPeriod",
    category: "purchaseTransaction",
  },
  {
    component: TotalGrossValueInLastNDays,
    initialValues: initialValuesTotalGrossValueInLastNDays,
    label: "totalSpendingInLastXDays",
    category: "purchaseTransaction",
  },
  {
    component: LastPurchaseWasNDaysAgo,
    initialValues: initialValuesLastPurchaseWasNDaysAgo,
    category: "purchaseTransaction",
    label: "purchaseTransactionWithinTheLastXNumberOfDays",
  },
  {
    component: PurchasePeriod,
    initialValues: initialValuesPurchasePeriod,
    category: "purchaseTransaction",
    label: "purchaseTransactionDateRange",
  },
  {
    component: TransactionValue,
    initialValues: initialValuesTransactionValue,
    category: "purchaseTransaction",
    label: "purchaseTransactionValueRange",
  },
  {
    component: NumberOfTransactionsInGivenPeriod,
    initialValues: initialValuesNumberOfTransactionsInGivenPeriod,
    category: "purchaseTransaction",
    label: "numberOfPurchaseTransactionsInGivenPeriod",
  },
  {
    component: TransactionCount,
    initialValues: initialValuesTransactionCount,
    category: "purchaseTransaction",
    label: "purchaseTransactionCountRange",
  },
  // custom event
  {
    component: LastCustomEventWasNDaysAgo,
    initialValues: initialValuesLastCustomEventWasNDaysAgo,
    label: "customEventWithinTheLastXNumberOfDays",
    category: "customEvent",
  },
  {
    component: LastCustomEventWithin,
    initialValues: initialValuesLastCustomEventWithin,
    label: "lastCustomEventWithinTheXToYNumberOfDays",
    category: "customEvent",
  },
];

const defaultPopularConditions: SegmentFECondition[] = [
  {
    component: AverageTransactionAmount,
    initialValues: initialValuesAverageTransactionAmount,
    label: "averagePurchaseTransactionValueRange",
  },
  {
    component: BoughtSpecificSKU,
    initialValues: initialValuesBoughtSpecificSKU,
    label: "boughtSpecificSKU",
  },
  {
    component: TotalGrossValueInGivenPeriod,
    initialValues: initialValuesTotalGrossValueInGivenPeriod,
    label: "totalSpendingInGivenPeriod",
  },
  {
    component: TransactionValue,
    initialValues: initialValuesTransactionValue,
    label: "purchaseTransactionValueRange",
  },
];

export const getComponent = (type: string) => {
  switch (type) {
    case "age":
      return Age;
    case "anniversary-birthday":
      return BirthdayAnniversary;
    case "anniversary-registration":
      return RegistrationAnniversary;
    case "member_registration_date":
      return MemberRegistrationDate;
    case "average_transaction_amount":
      return AverageTransactionAmount;
    case "bought_labels":
      return BoughtProductsWithLabels;
    case "bought_makers":
      return BoughtSpecificBrands;
    case "bought_skus":
      return BoughtSpecificSKU;
    case "city":
      return City;
    case "country":
      return CountryList;
    case "customer_list":
      return CustomMemberList;
    case "customer_with_labels_values":
      return MembersHaveSuchLabelsValues;
    case "customer_has_labels":
      return MembersWhoHaveSuchLabels;
    case "last_purchase_n_days_before":
      return LastPurchaseWasNDaysAgo;
    case "last_custom_event_n_days_before":
      return LastCustomEventWasNDaysAgo;
    case "last_transaction_between_days":
      return LastPurchaseWithin;
    case "last_custom_event_between_days":
      return LastCustomEventWithin;
    case "purchase_period":
      return PurchasePeriod;
    case "postal":
      return PostalCode;
    case "province":
      return Province;
    case "transaction_count":
      return TransactionCount;
    case "transaction_amount":
      return TransactionValue;
    case "gender":
      return Gender;
    case "transaction_percent_in_channel":
      return TransactionPercentInChannel;
    case "bought_through_channel":
      return BoughtInSpecificChannel;
    case "transaction_amount_each_day_in_period":
      return DailyGrossValueInGivenPeriod;
    case "transaction_amount_each_day_in_last_n_days":
      return DailyGrossValueInLastNDays;
    case "transaction_amount_in_period":
      return TotalGrossValueInGivenPeriod;
    case "transaction_amount_in_last_n_days":
      return TotalGrossValueInLastNDays;
    case "campaign_completion":
      return CampaignCompletion;
    case "achievement_rule_progress":
      return AchievementProgression;
    case "achievement_completion_count":
      return AchievementCompletion;
    case "transaction_count_in_period":
      return NumberOfTransactionsInGivenPeriod;
    case "customer_in_tiers":
      return MemberTiers;
  }
};

const getDefaultCondition = (type: string) => {
  const conditionLabel =
    segmentConditions.find(
      (condition) => condition.component === getComponent(type),
    )?.label || "";
  return conditionLabel;
};

const transformParts = (parts: SegmentFEParts[]): Parts[] =>
  parts.map((item) => {
    return {
      criteria: item.criteria.map(
        ({
          defaultValues,
          isConditionSaved,
          category,
          initialValues,
          label,
          ...rest
        }) => {
          switch (rest.type) {
            // As per BE requirements, change the type back to "anniversary"
            case "anniversary-birthday":
            case "anniversary-registration":
              return { ...rest, type: "anniversary" };
            // For customer_list condition remove segmentedCustomers field - BE returns this field in GET but doesn't accept in PUT
            case "customer_list":
              return { ...rest, segmentedCustomers: undefined };
            default:
              return { ...rest };
          }
        },
      ),
    } as Parts;
  });

export const maxVisibleConditionDataCount = 3;

export const halfWidthConditionTypes: Array<string> = [
  "achievement_completion_count",
  "achievement_rule_progress",
  "anniversary-birthday",
  "anniversary-registration",
  "anniversary-registration",
  "bought_through_channel",
  "gender",
  "last_custom_event_between_days",
  "last_purchase_n_days_before",
  "transaction_amount_each_day_in_last_n_days",
  "transaction_amount_in_last_n_days",
];

export const segmentTypes: SegmentType[] = [
  {
    segment: SegmentTypes.DYNAMIC,
    descriptionText: "dynamicSegmentDescription",
    label: "dynamicSegment",
  },
  {
    segment: SegmentTypes.STATIC,
    descriptionText: "staticSegmentDescription",
    label: "staticSegment",
  },
];

const staticSegmentCriterion = [
  {
    criteria: [{ type: "customer_csv_list" }],
  },
];

export function SegmentForm(props: {
  isEditMode?: boolean;
  initialValues: SegmentFEFields;
}) {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { addSegment, editSegment } = useSegments();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { id: segmentID } = useParams<{ id: string }>();
  const [isCommandBarOpen, setIsCommandBarOpen] = useState(false);
  const [onItemClick, setOnItemClick] = useState(() => (item: any) => {});

  return (
    <>
      <Formik
        enableReinitialize={props.isEditMode}
        initialStatus={{}}
        initialValues={props.initialValues}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          try {
            const isStaticSegment = values.type === SegmentTypes.STATIC;
            const segmentValues = omit(values, ["type"]);
            const segmentParts = isStaticSegment
              ? transformParts(staticSegmentCriterion)
              : transformParts(values.parts);

            const segmentData = {
              ...segmentValues,
              parts: segmentParts,
            };

            const response =
              props.isEditMode && props.initialValues
                ? await editSegment(segmentID as string, segmentData)
                : await addSegment(segmentData);

            if (response?.data?.segmentId) {
              if (!props.isEditMode && isStaticSegment) {
                navigate(links.segment(response?.data?.segmentId), {
                  state: { activeTab: "members", segmentName: values.name },
                });
              } else {
                navigate(
                  props.isEditMode
                    ? links.segmentsList
                    : `${links.segmentsList}${surveyQuery}`,
                );
              }

              notify(
                message({
                  id: props.isEditMode ? "segmentEdited" : "segmentAdded",
                }),
              );
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({
          values,
          handleSubmit,
          setValues,
          setSubmitting,
          isSubmitting,
          status,
          dirty,
        }) => (
          <Form onSubmit={handleSubmit}>
            {status?.[""] && (
              <span className={classes.error}>{status[""]}</span>
            )}
            <FormSection title={message({ id: "segmentType" })}>
              <Grid container spacing={12}>
                {segmentTypes.map((segmentType, index) => (
                  <Grid xs={6} item key={index}>
                    <ItemTypeCheckbox
                      activeItem={segmentType.segment === values.type}
                      label={message({ id: segmentType.label })}
                      description={message({ id: segmentType.descriptionText })}
                      data-test-id={`${segmentType.segment}-segment-type`}
                      onClick={() =>
                        setValues({
                          ...values,
                          type: segmentType.segment,
                          parts: [],
                        })
                      }
                    />
                  </Grid>
                ))}
                {values.type === SegmentTypes.STATIC && (
                  <Grid item xs={12}>
                    <Typography className={classes.segmentTypeInfo}>
                      {message(
                        { id: "staticSegmentInfo" },
                        {
                          link: (
                            <ExternalLink path="https://help.openloyalty.io/main-features/members/segments">
                              {message({ id: "learnMoreAboutSegments" })}
                            </ExternalLink>
                          ),
                        },
                      )}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </FormSection>
            <FormSection title={message({ id: "basicSettings" })}>
              <Grid container spacing={12}>
                <Grid xs={6} item>
                  <TextInput
                    width="100%"
                    data-test-id="name-input"
                    label={
                      <RequiredFieldLabel text={message({ id: "name" })} />
                    }
                    name="name"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextInput
                    width="100%"
                    data-test-id="description-input"
                    label={message({ id: "description" })}
                    maxRows={4}
                    multiline
                    name="description"
                  />
                </Grid>
              </Grid>
            </FormSection>
            {values.type === SegmentTypes.DYNAMIC && (
              <FormSection
                sectionError={typeof status?.parts === "string" && status.parts}
                title={message({ id: "conditions" })}
              >
                <FieldArray
                  name="parts"
                  render={({
                    remove,
                    push,
                    insert,
                    form: { values, setFieldValue },
                  }) => {
                    const { parts } = values;
                    const areConditionsAdded = parts && parts?.length > 0;

                    return (
                      <>
                        {areConditionsAdded ? (
                          <>
                            <div className={classes.flexColumn}>
                              {parts?.map((input: any, index: number) => {
                                return (
                                  <>
                                    <div
                                      key={index}
                                      className={classes.conditionWrapper}
                                    >
                                      <div
                                        className={`${classes.topFunctions} ${
                                          parts?.[index]?.hidden
                                            ? classes.topFunctionsHidden
                                            : ""
                                        }`}
                                      >
                                        <Typography
                                          className={classes.ruleHeader}
                                        >
                                          {message({ id: "rule" })} {index + 1}
                                        </Typography>
                                        <div className={classes.actionWrapper}>
                                          <Copy02Icon
                                            className={classes.bigIcon}
                                            data-test-id="segment-duplicate"
                                            onClick={() =>
                                              insert(index + 1, {
                                                ...parts?.[index],
                                              })
                                            }
                                          />
                                          <Trash02Icon
                                            className={classes.bigIcon}
                                            data-test-id="segment-delete"
                                            onClick={() => remove(index)}
                                          />
                                          <ChevronDownIcon
                                            onClick={() => {
                                              setFieldValue(
                                                `parts.${[index]}.hidden`,
                                                !parts?.[index]?.hidden,
                                              );
                                            }}
                                            className={`${classes.bigIcon} ${
                                              parts?.[index]?.hidden
                                                ? classes.iconReverted
                                                : ""
                                            }`}
                                            fontSize="small"
                                            data-test-id="segment-open-details"
                                          />
                                        </div>
                                      </div>
                                      <FieldArray
                                        name={`parts.${[index]}.criteria`}
                                        render={({
                                          remove,
                                          replace,
                                          insert,
                                          form: { values, setFieldValue },
                                        }) => {
                                          const hidden = parts?.[index]?.hidden;
                                          const conditionCirteria =
                                            values?.parts?.[index]?.criteria;
                                          const conditionStatus =
                                            status?.parts?.[index]?.criteria;
                                          return (
                                            <>
                                              <div
                                                className={`${
                                                  classes.conditionsHeaderContainer
                                                } ${
                                                  hidden ? classes.hidden : ""
                                                }`}
                                              >
                                                <Typography
                                                  className={
                                                    classes.conditionHeader
                                                  }
                                                >
                                                  {message({
                                                    id: "conditions",
                                                  })}
                                                </Typography>
                                                <OLButton
                                                  className={
                                                    classes.addConditionButton
                                                  }
                                                  data-test-id={`segment-condition-${index}-add-btn`}
                                                  onClick={() => {
                                                    setOnItemClick(
                                                      () =>
                                                        (item: {
                                                          initialValues: any;
                                                          component: any;
                                                        }) => {
                                                          insert(0, {
                                                            ...item?.initialValues,
                                                            component:
                                                              item?.component,
                                                          });
                                                        },
                                                    );
                                                    setIsCommandBarOpen(true);
                                                  }}
                                                  size="small"
                                                  startIcon={<PlusIcon />}
                                                  variant="outlined"
                                                >
                                                  {message({
                                                    id: "orCondition",
                                                  })}
                                                </OLButton>
                                              </div>
                                              {conditionCirteria?.map(
                                                (
                                                  criterion: any,
                                                  criterionIndex: number,
                                                ) => {
                                                  const CriterionComponent =
                                                    criterion?.component ||
                                                    getComponent(
                                                      criterion?.type,
                                                    );
                                                  const currentConditionError =
                                                    // It is required to verify status is not a string
                                                    !_.isString(
                                                      status?.parts,
                                                    ) &&
                                                    conditionStatus?.[
                                                      criterionIndex
                                                    ];
                                                  const isConditionSaved =
                                                    criterion?.isConditionSaved;
                                                  return (
                                                    <>
                                                      {isConditionSaved ? (
                                                        <div
                                                          className={`${
                                                            classes.readOnlyWrapper
                                                          } ${
                                                            hidden
                                                              ? classes.hidden
                                                              : ""
                                                          } ${
                                                            criterionIndex === 0
                                                              ? classes.firstReadOnlyWrapper
                                                              : ""
                                                          } ${
                                                            criterionIndex +
                                                              1 ===
                                                            conditionCirteria?.length
                                                              ? classes.lastReadOnlyWrapper
                                                              : ""
                                                          }`}
                                                        >
                                                          {`${
                                                            criterionIndex + 1
                                                          }.`}
                                                          <div
                                                            className={
                                                              classes.boxesWrapper
                                                            }
                                                          >
                                                            <div
                                                              className={
                                                                classes.box
                                                              }
                                                            >
                                                              {message({
                                                                id: getDefaultCondition(
                                                                  conditionCirteria?.[
                                                                    criterionIndex
                                                                  ]?.type,
                                                                ),
                                                              })}
                                                            </div>
                                                            <CriterionComponent
                                                              values={values}
                                                              formField={`parts.${index}.criteria.${criterionIndex}`}
                                                              status={
                                                                conditionStatus?.[
                                                                  criterionIndex
                                                                ]
                                                              }
                                                              setFieldValue={(
                                                                field: string,
                                                                value: any,
                                                              ) =>
                                                                setFieldValue(
                                                                  field,
                                                                  value,
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                          <div
                                                            className={
                                                              classes.iconsWrapper
                                                            }
                                                          >
                                                            {currentConditionError && (
                                                              <div
                                                                className={
                                                                  classes.iconBox
                                                                }
                                                              >
                                                                <AlertTriangleIcon
                                                                  className={
                                                                    classes.errorIcon
                                                                  }
                                                                  data-test-id="segment-error"
                                                                />
                                                              </div>
                                                            )}
                                                            <div
                                                              className={
                                                                classes.iconBox
                                                              }
                                                            >
                                                              <Pencil02Icon
                                                                className={
                                                                  classes.icon
                                                                }
                                                                data-test-id="segment-edit"
                                                                onClick={() =>
                                                                  setFieldValue(
                                                                    `parts.${index}.criteria.${criterionIndex}`,
                                                                    {
                                                                      ...conditionCirteria?.[
                                                                        criterionIndex
                                                                      ],
                                                                      // defaultValues - copy of values already saved, used with "cancel" button as default values
                                                                      defaultValues:
                                                                        conditionCirteria?.[
                                                                          criterionIndex
                                                                        ],
                                                                      isConditionSaved:
                                                                        false,
                                                                    },
                                                                  )
                                                                }
                                                              />
                                                            </div>
                                                            <div
                                                              className={
                                                                classes.iconBox
                                                              }
                                                            >
                                                              <Copy02Icon
                                                                className={
                                                                  classes.icon
                                                                }
                                                                data-test-id="segment-duplicate"
                                                                onClick={() =>
                                                                  insert(
                                                                    criterionIndex +
                                                                      1,
                                                                    {
                                                                      ...conditionCirteria?.[
                                                                        criterionIndex
                                                                      ],
                                                                      criterionId:
                                                                        undefined,
                                                                      isConditionSaved:
                                                                        false,
                                                                    },
                                                                  )
                                                                }
                                                              />
                                                            </div>
                                                            <div
                                                              className={
                                                                classes.iconBox
                                                              }
                                                            >
                                                              <XCloseIcon
                                                                className={
                                                                  classes.icon
                                                                }
                                                                data-test-id="segment-delete"
                                                                onClick={() =>
                                                                  remove(
                                                                    criterionIndex,
                                                                  )
                                                                }
                                                              />
                                                            </div>
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <section
                                                          className={`${
                                                            classes.conditionSectionEdited
                                                          } ${
                                                            hidden
                                                              ? classes.hidden
                                                              : ""
                                                          }  ${
                                                            criterionIndex +
                                                              1 ===
                                                            conditionCirteria?.length
                                                              ? classes.conditionSectionEditedIsLastCondition
                                                              : ""
                                                          }`}
                                                        >
                                                          <Typography
                                                            className={
                                                              classes.conditionHeader
                                                            }
                                                          >
                                                            {message({
                                                              id: "newCondition",
                                                            })}
                                                          </Typography>
                                                          <div
                                                            className={
                                                              classes.singleConditionRow
                                                            }
                                                          >
                                                            <SelectInput
                                                              label={
                                                                <RequiredFieldLabel
                                                                  text={message(
                                                                    {
                                                                      id: "conditionType",
                                                                    },
                                                                  )}
                                                                />
                                                              }
                                                              displaySearch
                                                              className={
                                                                halfWidthConditionTypes.includes(
                                                                  conditionCirteria?.[
                                                                    criterionIndex
                                                                  ]?.type,
                                                                )
                                                                  ? classes.selectInputHalfWidth
                                                                  : ""
                                                              }
                                                              name={`parts.${index}.criteria.${criterionIndex}.type`}
                                                              initialValue={{
                                                                id: conditionCirteria?.[
                                                                  criterionIndex
                                                                ]?.type,
                                                                label: message({
                                                                  id: getDefaultCondition(
                                                                    conditionCirteria?.[
                                                                      criterionIndex
                                                                    ]?.type,
                                                                  ),
                                                                }),
                                                              }}
                                                              items={
                                                                alphabetizeByProperty(
                                                                  [
                                                                    ...segmentConditions.map(
                                                                      (
                                                                        condition,
                                                                      ) => {
                                                                        return {
                                                                          label:
                                                                            message(
                                                                              {
                                                                                id: condition.label,
                                                                              },
                                                                            ),
                                                                          id: segmentConditions.find(
                                                                            (
                                                                              segment,
                                                                            ) =>
                                                                              condition.label ===
                                                                              segment?.label,
                                                                          )
                                                                            ?.initialValues
                                                                            ?.type,
                                                                        };
                                                                      },
                                                                    ),
                                                                  ],
                                                                  "label",
                                                                ) || []
                                                              }
                                                              data-test-id="segment-type"
                                                              onChange={(e) => {
                                                                const newInputValue =
                                                                  e.target;
                                                                const selected =
                                                                  segmentConditions.find(
                                                                    (
                                                                      condition,
                                                                    ) =>
                                                                      condition
                                                                        .initialValues
                                                                        ?.type ===
                                                                      newInputValue?.value,
                                                                  );
                                                                if (selected) {
                                                                  const newValue =
                                                                    {
                                                                      ...selected.initialValues,
                                                                      component:
                                                                        selected.component,
                                                                    };

                                                                  replace(
                                                                    criterionIndex,
                                                                    newValue,
                                                                  );
                                                                }
                                                              }}
                                                            />
                                                            <CriterionComponent
                                                              formField={`parts.${index}.criteria.${criterionIndex}`}
                                                              status={
                                                                conditionStatus?.[
                                                                  criterionIndex
                                                                ]
                                                              }
                                                              values={values}
                                                              setFieldValue={(
                                                                field: string,
                                                                value: any,
                                                              ) =>
                                                                setFieldValue(
                                                                  field,
                                                                  value,
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                          <div
                                                            className={
                                                              classes.buttonWrapper
                                                            }
                                                          >
                                                            <OLButton
                                                              data-test-id={`segment-condition-${index}-cancel-btn`}
                                                              onClick={() => {
                                                                // restore defaultValues if defined, if not - remove condition
                                                                _.get(
                                                                  values,
                                                                  `parts.${index}.criteria.${criterionIndex}.defaultValues`,
                                                                )
                                                                  ? setFieldValue(
                                                                      `parts.${index}.criteria.${criterionIndex}`,
                                                                      {
                                                                        ...conditionCirteria?.[
                                                                          criterionIndex
                                                                        ],
                                                                        defaultValues:
                                                                          undefined,
                                                                        isConditionSaved:
                                                                          true,
                                                                      },
                                                                    )
                                                                  : remove(
                                                                      criterionIndex,
                                                                    );
                                                              }}
                                                              size="small"
                                                            >
                                                              {message({
                                                                id: "cancel",
                                                              })}
                                                            </OLButton>
                                                            <OLButton
                                                              className={
                                                                classes.saveConditionButton
                                                              }
                                                              color="primary"
                                                              data-test-id={`segment-condition-${index}-save-btn`}
                                                              onClick={() => {
                                                                setFieldValue(
                                                                  `parts.${index}.criteria.${criterionIndex}`,
                                                                  {
                                                                    ...conditionCirteria?.[
                                                                      criterionIndex
                                                                    ],
                                                                    // clear defaultValues just in case
                                                                    defaultValues:
                                                                      undefined,
                                                                    isConditionSaved:
                                                                      true,
                                                                  },
                                                                );
                                                              }}
                                                              size="small"
                                                              variant="contained"
                                                            >
                                                              {message({
                                                                id: "saveCondition",
                                                              })}
                                                            </OLButton>
                                                          </div>
                                                        </section>
                                                      )}
                                                      {criterionIndex + 1 <
                                                        conditionCirteria?.length && (
                                                        <Box
                                                          className={`${
                                                            classes.grayText
                                                          } ${
                                                            hidden
                                                              ? classes.hidden
                                                              : ""
                                                          }`}
                                                          ml={8}
                                                        >
                                                          {message({
                                                            id: "or",
                                                          }).toUpperCase()}
                                                        </Box>
                                                      )}
                                                    </>
                                                  );
                                                },
                                              )}
                                            </>
                                          );
                                        }}
                                      />
                                    </div>
                                    {index + 1 < values?.parts?.length && (
                                      <span className={classes.grayTextAnd}>
                                        <Box mb={12} />
                                        {message({
                                          id: "and",
                                        }).toUpperCase()}
                                        <Box mb={12} />
                                      </span>
                                    )}
                                  </>
                                );
                              })}
                            </div>
                            <Box mb={12} />
                            <OLButton
                              data-test-id="add-condition-btn"
                              onClick={() => {
                                setOnItemClick(
                                  () =>
                                    (item: {
                                      initialValues: any;
                                      component: any;
                                    }) => {
                                      push({
                                        criteria: [
                                          {
                                            ...item?.initialValues,
                                            component: item?.component,
                                          },
                                        ],
                                      });
                                    },
                                );
                                setIsCommandBarOpen(true);
                              }}
                              size="small"
                              variant="outlined"
                            >
                              {message(
                                { id: "plusMore" },
                                { more: message({ id: "andCondition" }) },
                              )}
                            </OLButton>
                          </>
                        ) : (
                          <EmptyState
                            icon={
                              <FilePlus03Icon className={classes.iconAdd} />
                            }
                            title={message({ id: "addFirstCondition" })}
                            description={message({
                              id: "firstSegmentConditionInfo",
                            })}
                            button={
                              <OLButton
                                className={classes.addConditionButton}
                                data-test-id="add-condition-btn"
                                onClick={() => {
                                  setOnItemClick(() => (item: any) => {
                                    push({
                                      criteria: [
                                        {
                                          ...item?.initialValues,
                                          component: item?.component,
                                        },
                                      ],
                                    });
                                  });
                                  setIsCommandBarOpen(true);
                                }}
                                size="small"
                                startIcon={<PlusIcon />}
                                variant="contained"
                              >
                                {message({ id: "addCondition" })}
                              </OLButton>
                            }
                          />
                        )}
                        <CommandBar
                          conditions={segmentConditions}
                          defaultPopularConditions={defaultPopularConditions}
                          isOpen={isCommandBarOpen}
                          onClose={() => setIsCommandBarOpen(false)}
                          onItemClick={onItemClick}
                          storagedConditionsName="segmentPopularConditions"
                        />
                      </>
                    );
                  }}
                />
              </FormSection>
            )}
            <FormSection title={message({ id: "segmentStatus" })}>
              <Toggle
                data-test-id="segment-is-active"
                label={message({ id: "active" })}
                name="active"
              />
              <Box mt={8} />
              <Typography className={classes.subtitle1}>
                {message({ id: "segmentActivityInfo" })}
              </Typography>
              {props.isEditMode && (
                <Typography className={classes.subtitle1}>
                  {message(
                    { id: "beforeDeactivationPleaseReview" },
                    {
                      link: (
                        <ExternalLink
                          path={links.deactivatedSegment(segmentID)}
                          className={classes.reviewLink}
                        >
                          {message({
                            id: "associatedResourcesHere",
                          }).toLocaleLowerCase()}
                        </ExternalLink>
                      ),
                    },
                  )}
                </Typography>
              )}
            </FormSection>
            <Box mt={12} />
            <FormButtons
              disableSubmit={isSubmitting}
              onCancelClick={() => navigate(links.segmentsList)}
              onSubmitClick={() => handleSubmit()}
              {...(!props.isEditMode && { submitMessage: "addSegment" })}
            />
            {!isSubmitting && dirty && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={props.initialValues}
                currentValues={values}
                data-test-id="segment"
              />
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}
